import React, { useEffect, useState } from 'react';
import { CardContent } from '@/common/ui/card';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'common/components/LoadingSpinner';
import userContextSlice from 'common/slices/userContextSlice';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { useApiPortalV1AssessmentsPlansOrganizationListQuery } from 'areas/assessment-schedule/api';
import { RecommendationType } from 'common/constants/RecommendationType';
import TeamProject from 'areas/team-recommendations/components/TeamProject';
import { PageContent, PageHeader, PageSection } from '@/common/styles/page';
import { Clock } from 'lucide-react';

export const TeamProjects: React.FC = () => {
    const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
    const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId) as number | null;

    // Fetch all team projects for the selected assessment period; we will filter client-side by team
    const {
        data: teamPlans,
        isLoading: isLoadingPlans,
        isFetching: isFetchingPlans,
        error: plansError,
    } = useApiPortalV1AssessmentsPlansOrganizationListQuery(
        {
            assessmentPeriodId: selectedAssessmentPeriodId ?? undefined,
            typeOf: RecommendationType.TEAM_RECOMMENDATIONS,
        },
        { skip: !selectedAssessmentPeriodId }
    );

    const [notFoundForSelection, setNotFoundForSelection] = useState(false);

    const [foundPlanId, setFoundPlanId] = useState<number | null>(null);

    useEffect(() => {
        // Only attempt to locate a plan if we have both selected team and assessment period
        if (!selectedTeamId || !selectedAssessmentPeriodId) {
            setNotFoundForSelection(false);
            setFoundPlanId(null);
            return;
        }

        if (isLoadingPlans || isFetchingPlans) return;

        const found = (teamPlans || []).find((p: any) => Number(p.team) === Number(selectedTeamId));

        if (found) {
            setFoundPlanId(found.id);
            setNotFoundForSelection(false);
        } else {
            // No matching plan found for selected team + assessment period
            setFoundPlanId(null);
            setNotFoundForSelection(true);
        }
    }, [selectedTeamId, selectedAssessmentPeriodId, teamPlans, isLoadingPlans, isFetchingPlans]);

    if (!selectedAssessmentPeriodId) {
        // No assessment period selected yet — show placeholder
        return (
            <div style={{ minHeight: 'calc(100vh - 150px)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <CardContent className="p-6">
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 text-center">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Team Projects</h2>
                        <p className="text-gray-600">Please select an assessment period in the top bar to view team projects.</p>
                    </div>
                </CardContent>
            </div>
        );
    }

    if (isLoadingPlans) {
        return <LoadingSpinner />;
    }

    return (
        <>
            {foundPlanId ? (
                <PageContent>
                    <PageSection>
                        <TeamProject actionPlanId={foundPlanId} />
                    </PageSection>
                </PageContent>
            ) : (
                <PageContent>
                    <PageSection padleft padright>
                        <PageHeader title="Team Projects" />

                        {selectedTeamId && notFoundForSelection && (
                            <PageSection display='flex' flexDirection='row' gap='20px' alignItems='center' justifyContent='center' padtop>
                                <Clock className="w-12 h-12 text-gray-400 flex-shrink-0" />
                                <PageSection display='flex' flexDirection='column' gap='4px'>
                                    <span className="text-base">No Team Project Assigned</span>
                                    <span className="text-sm text-gray-400">No team project has been assigned for the selected team and assessment period.</span>
                                </PageSection>
                            </PageSection>
                        )}
                    </PageSection>
                </PageContent>
            )}
        </>
    );
};

export default TeamProjects;
